import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useStripeCheckout } from '@/lib/useStripeCheckout';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, Clock, AlertCircle, Copy, Check, CreditCard, QrCode, Sparkles, ImageIcon, Zap } from 'lucide-react';

type Plan = { credits: number; priceCentavos: number; label: string };

const PLANS: Plan[] = [
  { credits: 5,  priceCentavos: 2490, label: 'Starter' },
  { credits: 10, priceCentavos: 4990, label: 'Pro' },
];

type PaymentMethod = 'select' | 'pix';
type PixState = 'loading' | 'showing_qr' | 'paid' | 'expired' | 'error';

type ChargeResponse = {
  id: string;
  qrCode: string;
  qrCodeBase64: string;
  expiresAt: string;
  status: string;
  amountCentavos: number;
  creditsAmount: number;
};

type StatusResponse = {
  status: 'pending' | 'paid' | 'expired';
  expiresAt?: string;
  credits?: number;
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatPrice(centavos: number): string {
  return (centavos / 100).toFixed(2).replace('.', ',');
}

export function PaymentPage() {
  const navigate = useNavigate();
  const { userData, refreshUserData } = useAuth();
  const { startCheckout, loading: stripeLoading, error: stripeError } = useStripeCheckout();

  const [method, setMethod] = useState<PaymentMethod>('select');
  const [selectedPlan, setSelectedPlan] = useState<Plan>(PLANS[0]);
  const [pixState, setPixState] = useState<PixState>('loading');
  const [charge, setCharge] = useState<ChargeResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const PHOTOS_PER_CREDIT = 3;

  const createCharge = useCallback(async (plan: Plan) => {
    setPixState('loading');
    setCharge(null);
    setErrorMessage(null);

    try {
      const data = await api.post<ChargeResponse>('/api/payments/pix', { credits: plan.credits });
      setCharge(data);
      setPixState('showing_qr');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Erro ao gerar cobranca PIX');
      setPixState('error');
    }
  }, []);

  const selectPix = useCallback((plan: Plan) => {
    setSelectedPlan(plan);
    setMethod('pix');
    createCharge(plan);
  }, [createCharge]);

  // Poll payment status every 3s
  useEffect(() => {
    if (method !== 'pix' || pixState !== 'showing_qr' || !charge) return;

    const interval = setInterval(async () => {
      try {
        const data = await api.get<StatusResponse>(`/api/payments/${charge.id}/status`);
        if (data.status === 'paid') {
          setPixState('paid');
          setCredits(data.credits ?? null);
          refreshUserData();
          clearInterval(interval);
        } else if (data.status === 'expired') {
          setPixState('expired');
          clearInterval(interval);
        }
      } catch {
        // silent
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [method, pixState, charge, refreshUserData]);

  // Countdown timer
  useEffect(() => {
    if (!charge?.expiresAt) return;
    const expiresAt = new Date(charge.expiresAt).getTime();

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) setPixState('expired');
    };

    updateRemaining();
    const timer = setInterval(updateRemaining, 1000);
    return () => clearInterval(timer);
  }, [charge?.expiresAt]);

  const handleCopy = async () => {
    if (!charge) return;
    try {
      await navigator.clipboard.writeText(charge.qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silent
    }
  };

  // PIX paid state
  if (method === 'pix' && pixState === 'paid') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center px-4">
          <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Pagamento confirmado!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-1">
            Seus creditos foram adicionados com sucesso.
          </p>
          {credits !== null && (
            <p className="text-lg font-semibold text-green-600 dark:text-green-400 mb-6">
              {credits} creditos disponiveis
            </p>
          )}
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3"
            onClick={() => navigate('/new')}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Gerar minhas fotos
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Method selection screen
  if (method === 'select') {
    return (
      <DashboardLayout>
        <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

          <div className="text-center">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Comprar Creditos</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Cada credito gera 1 sessao com {PHOTOS_PER_CREDIT} fotos profissionais
            </p>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-2 gap-3">
            {PLANS.map((plan) => {
              const isSelected = selectedPlan.credits === plan.credits;
              const pricePerPhoto = formatPrice(Math.round(plan.priceCentavos / (plan.credits * PHOTOS_PER_CREDIT)));
              return (
                <button
                  key={plan.credits}
                  onClick={() => setSelectedPlan(plan)}
                  className={`relative rounded-2xl border-2 p-4 text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300'
                  }`}
                >
                  {plan.credits === 10 && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                      <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                        MAIS POPULAR
                      </span>
                    </div>
                  )}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {plan.label}
                    </p>
                    <div className="flex items-baseline gap-0.5 mt-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">R$</span>
                      <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {formatPrice(plan.priceCentavos).split(',')[0]}
                      </span>
                      <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                        ,{formatPrice(plan.priceCentavos).split(',')[1]}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {plan.credits} creditos
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <ImageIcon className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {plan.credits * PHOTOS_PER_CREDIT} fotos no total
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Zap className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        R$ {pricePerPhoto}/foto
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                      <Check className="h-2.5 w-2.5 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Current credits */}
          {userData && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Saldo atual:{' '}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {userData.credits ?? 0} creditos
                </span>
              </p>
            </div>
          )}

          {/* Payment Methods */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
              Pagar {formatPrice(selectedPlan.priceCentavos)} com:
            </p>

            {stripeError && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg p-3 text-center">
                {stripeError}
              </div>
            )}

            {/* Credit Card */}
            <button
              onClick={() => startCheckout(selectedPlan.credits)}
              disabled={stripeLoading}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-500 dark:hover:border-blue-400 transition-all hover:shadow-md text-left disabled:opacity-60"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                {stripeLoading ? (
                  <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : (
                  <CreditCard className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  Cartao de Credito
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Visa, Mastercard — aprovacao imediata
                </p>
              </div>
            </button>

            {/* PIX */}
            <button
              onClick={() => selectPix(selectedPlan)}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-500 dark:hover:border-green-400 transition-all hover:shadow-md text-left"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                <QrCode className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  PIX
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pagamento instantaneo via QR Code
                </p>
              </div>
            </button>
          </div>

        </div>
      </DashboardLayout>
    );
  }

  // PIX flow
  return (
    <DashboardLayout>
      <div className="max-w-md mx-auto px-4 py-4 space-y-6">
        {/* Back button */}
        <button
          onClick={() => { setMethod('select'); setPixState('loading'); setCharge(null); }}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          &larr; Voltar
        </button>

        {/* PIX Loading */}
        {pixState === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="h-12 w-12 text-green-600 animate-spin mb-4" />
            <p className="text-lg font-medium text-gray-800 dark:text-gray-200">
              Gerando QR Code PIX...
            </p>
          </div>
        )}

        {/* PIX Error */}
        {pixState === 'error' && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {errorMessage || 'Erro ao processar pagamento'}
            </p>
            <Button variant="outline" onClick={() => createCharge(selectedPlan)}>
              Tentar novamente
            </Button>
          </div>
        )}

        {/* PIX Expired */}
        {pixState === 'expired' && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Clock className="h-12 w-12 text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              QR Code expirado
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              O tempo para pagamento expirou.
            </p>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3"
              onClick={() => createCharge(selectedPlan)}
            >
              Gerar novo QR Code
            </Button>
          </div>
        )}

        {/* PIX QR Code */}
        {pixState === 'showing_qr' && charge && (
          <>
            {/* Summary */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Pacote de Creditos</span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  R$ {formatPrice(charge.amountCentavos)}
                </span>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span>{charge.creditsAmount} creditos</span>
                  <span>{charge.creditsAmount * PHOTOS_PER_CREDIT} fotos profissionais</span>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-white p-4 rounded-xl shadow-md">
                <img
                  src={charge.qrCodeBase64}
                  alt="QR Code PIX"
                  className="w-64 h-64"
                />
              </div>

              {timeLeft !== null && timeLeft > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Expira em{' '}
                  <span className="font-mono font-semibold text-gray-700 dark:text-gray-300">
                    {formatTime(timeLeft)}
                  </span>
                </p>
              )}
            </div>

            {/* Copia e Cola */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                PIX Copia e Cola
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={charge.qrCode}
                  className="flex-1 min-w-0 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 overflow-hidden text-ellipsis"
                />
                <Button
                  variant="outline"
                  className="shrink-0 gap-2"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <><Check className="h-4 w-4 text-green-500" /> Copiado!</>
                  ) : (
                    <><Copy className="h-4 w-4" /> Copiar</>
                  )}
                </Button>
              </div>
            </div>

            {/* Waiting indicator */}
            <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-sm">Aguardando pagamento...</span>
            </div>

            <p className="text-center text-xs text-gray-400 dark:text-gray-500">
              O pagamento e detectado automaticamente assim que confirmado
            </p>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
